import {
  documentTitleFromUploadFileName,
  isAcceptableTextUpload,
  isExcelUpload,
  isPdfUpload,
  isWordUpload,
  sanitizeUploadFileName,
  utf8TextLooksCorrupted,
} from '../../shared/upload_policy'
import { processExcelUploadFile } from '../../shared/process_excel_upload'
import { processWordUploadFile } from '../../shared/process_word_upload'
import { arrayBufferToBase64 } from '../../shared/bytes_base64'
import { extractPdfTextFromArrayBuffer } from './extractPdfText'

/** Shown when pdf.js fails; upload still succeeds if we stored a preview blob. */
const PDF_EXTRACT_BROWSER_FALLBACK =
  '(PDF text extraction failed in the browser—often scanned PDFs, uncommon encodings, or a pdf.js issue. If preview works below, you can still read the file. For reliable extraction, try the desktop app or re-export the PDF.)'

export type ProcessedBrowserUpload = {
  originalName: string
  title: string
  content: string
  mimeType: string
  /** Base64 of original file bytes (PDF, Word, Excel); stored in hosted `blob` for on-the-fly preview. */
  blob?: string
}

export type ProcessBrowserUploadFailure = { originalName: string; error: string }

async function processPdfBrowserUpload(
  file: File,
  originalName: string,
): Promise<ProcessedBrowserUpload | ProcessBrowserUploadFailure> {
  let data: ArrayBuffer
  try {
    data = await file.arrayBuffer()
  } catch {
    return { originalName, error: 'read_failed' }
  }
  const title = documentTitleFromUploadFileName(originalName)

  let blob: string | undefined
  try {
    blob = arrayBufferToBase64(data)
  } catch {
    blob = undefined
  }

  let extracted: string
  try {
    extracted = await extractPdfTextFromArrayBuffer(data.slice(0))
  } catch {
    extracted = PDF_EXTRACT_BROWSER_FALLBACK
  }

  const content = extracted.trim().length === 0 ? ' ' : extracted
  const out: ProcessedBrowserUpload = {
    originalName,
    title,
    content,
    mimeType: 'application/pdf',
  }
  if (blob && blob.length > 0) {
    out.blob = blob
  }
  return out
}

export async function processBrowserUploadFile(file: File): Promise<ProcessedBrowserUpload | ProcessBrowserUploadFailure> {
  const originalName = sanitizeUploadFileName(file.name)
  const mimeType = (file.type || 'application/octet-stream').trim()

  if (isPdfUpload(originalName, mimeType)) {
    return processPdfBrowserUpload(file, originalName)
  }

  if (isExcelUpload(originalName, mimeType)) {
    return processExcelUploadFile(file, originalName)
  }

  if (isWordUpload(originalName, mimeType)) {
    return processWordUploadFile(file, originalName)
  }

  if (isAcceptableTextUpload(originalName, mimeType)) {
    let text: string
    try {
      text = await file.text()
    } catch {
      return { originalName, error: 'read_failed' }
    }

    if (utf8TextLooksCorrupted(text)) {
      return { originalName, error: 'invalid_utf8_or_binary' }
    }

    const title = documentTitleFromUploadFileName(originalName)
    const content = text.length === 0 ? ' ' : text

    return { originalName, title, content, mimeType: mimeType || 'text/plain' }
  }

  // Any non-text/* file we don't have a dedicated extractor for: keep the
  // original bytes as a blob so it can still be previewed/downloaded.
  let data: ArrayBuffer
  try {
    data = await file.arrayBuffer()
  } catch {
    return { originalName, error: 'read_failed' }
  }
  let binaryBlob: string | undefined
  try {
    binaryBlob = arrayBufferToBase64(data)
  } catch {
    return { originalName, error: 'read_failed' }
  }

  const out: ProcessedBrowserUpload = {
    originalName,
    title: documentTitleFromUploadFileName(originalName),
    content: ' ',
    mimeType: mimeType || 'application/octet-stream',
  }
  if (binaryBlob && binaryBlob.length > 0) {
    out.blob = binaryBlob
  }
  return out
}
